import type { ChartOption } from '@app-types/chart';
import type {
  GroupedFinancialChartData,
  PerformanceHistoryChartData,
} from '@app-types/performanceTrends';
import { CHART_AXIS_LABEL_STYLE, CHART_COLORS } from './chartConstants';

const MILLION = 1_000_000;
const MAX_AXIS_LABEL_DECIMALS = 12;

/**
 * Minimal number of decimal places needed to render `value` without dropping
 * significant digits, while tolerating tiny floating-point drift and capping
 * precision so we never emit runaway digits or scientific notation.
 */
const minimalDecimals = (value: number): number => {
  let decimals = 0;
  let scaled = Math.abs(value);
  while (
    decimals < MAX_AXIS_LABEL_DECIMALS &&
    Math.abs(scaled - Math.round(scaled)) > 1e-9 * Math.max(1, scaled)
  ) {
    scaled *= 10;
    decimals += 1;
  }
  return decimals;
};

/**
 * Uniform y-axis label for every Performance Trends chart, identical for JP and
 * US: it abbreviates one million of the chart's numeric units as an uppercase
 * `M`, e.g. `12,000,000 -> "12M"`, `500,000 -> "0.5M"`, `25 -> "0.000025M"`,
 * `0 -> "0M"`, `-500,000 -> "-0.5M"`. Only this label text is divided by one
 * million; series values, axis bounds, tooltips, and tables keep their original
 * units. The trailing `M` does not replace the API currency/unit shown in chart
 * titles and table headers.
 */
export const formatMillionsAxisLabel = (value: number): string => {
  if (!Number.isFinite(value)) return '';
  if (value === 0) return '0M';
  const scaled = value / MILLION;
  return `${scaled.toFixed(minimalDecimals(scaled))}M`;
};

/**
 * Single red vertical bar chart used by the annual performance history cards
 * (see PT03 / section 6.1). Quarterly uses quarterlyPeriodOptions instead.
 */
export function buildPerformanceHistoryOption(
  data: PerformanceHistoryChartData
): ChartOption {
  return {
    grid: { left: 8, right: 8, top: 8, bottom: 8, containLabel: true },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      position: 'bottom',
      data: data.points.map((p) => p.label),
      axisTick: { show: false },
      // Keep period labels below the plot when the signed Y range crosses zero.
      axisLine: { onZero: false, lineStyle: { color: CHART_COLORS.grid } },
      axisLabel: { ...CHART_AXIS_LABEL_STYLE, rotate: 90, interval: 0 },
    },
    yAxis: {
      type: 'value',
      min: data.min ?? 0,
      max: data.max,
      interval: data.interval,
      axisLabel: { ...CHART_AXIS_LABEL_STYLE, formatter: (v: number) => formatMillionsAxisLabel(v) },
      splitLine: { show: true, lineStyle: { color: CHART_COLORS.grid } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'bar',
        data: data.points.map((p) => p.value),
        itemStyle: { color: CHART_COLORS.seriesPrimary },
        barWidth: '8',
      },
    ],
  };
}

/**
 * Shared grouped bar chart for Balance sheet, Cash flow and R&D sections
 * (see PT03 / section 6.2–6.4). Supports negative values with a visible zero
 * line. The DOM ChartLegend renders labels, so no ECharts legend is used.
 */
export function buildGroupedFinancialOption(
  data: GroupedFinancialChartData
): ChartOption {
  return {
    grid: { left: 8, right: 8, top: 12, bottom: 8, containLabel: true },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: data.labels,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: CHART_COLORS.grid } },
      axisLabel: CHART_AXIS_LABEL_STYLE,
    },
    yAxis: {
      type: 'value',
      min: data.yAxisMin ?? 0,
      max: data.yAxisMax,
      interval: data.yAxisInterval,
      axisLabel: { ...CHART_AXIS_LABEL_STYLE, formatter: (v: number) => formatMillionsAxisLabel(v) },
      splitLine: { lineStyle: { color: CHART_COLORS.grid } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: data.series.map((s) => ({
      type: 'bar' as const,
      name: s.label,
      data: s.values,
      itemStyle: { color: s.color },
      barGap: '10%',
    })),
  };
}
